/**
 * Bottom tab bar configuration.
 *
 * Lives here rather than inline in app/(app)/_layout.tsx so the constraint
 * below can be enforced by a test — app/ is outside the Jest roots.
 *
 * #531: five tabs on a 344px phone gave each one ~68px, which clipped the
 * French labels ("Garde-manger" needs 84px, "Paramètres" 66px). Four tabs
 * give ~86px. Settings moved to HeaderAvatarButton. Adding a fifth tab
 * reintroduces the bug — see MAX_TABS.
 */

import type { ComponentProps } from 'react';
import type Feather from '@expo/vector-icons/Feather';

type FeatherIconName = ComponentProps<typeof Feather>['name'];

export interface TabDefinition {
  /** expo-router route name under app/(app)/ */
  name: string;
  /** i18n key for the tab label */
  labelKey: string;
  icon: FeatherIconName;
}

/**
 * The narrowest viewport we support, taken from the #531 bug report
 * (iPhone SE-class screen in Safari).
 */
export const NARROWEST_SUPPORTED_WIDTH = 344;

/**
 * A tab label needs roughly this much room at Inter-Medium 12px before it
 * clips. Derived from measuring the French labels at 344px.
 */
export const MIN_COMFORTABLE_TAB_WIDTH = 80;

/**
 * Hard ceiling on visible tabs, so the bottom bar can never again be packed
 * tighter than a French label can render.
 */
export const MAX_TABS = Math.floor(NARROWEST_SUPPORTED_WIDTH / MIN_COMFORTABLE_TAB_WIDTH);

export const VISIBLE_TABS: TabDefinition[] = [
  { name: 'index', labelKey: 'navigation.myMeals', icon: 'calendar' },
  { name: 'recipes', labelKey: 'navigation.recipes', icon: 'book-open' },
  { name: 'grocery-list', labelKey: 'navigation.grocery', icon: 'shopping-cart' },
  { name: 'inventory', labelKey: 'navigation.pantry', icon: 'package' },
];

/**
 * Routes registered with the tab navigator but hidden from the bar
 * (reachable by navigation only). `settings` sits here as of #531.
 */
export const HIDDEN_ROUTES: string[] = [
  'settings',
  'userProfile',
  // Undeclared routes are auto-added to the bar by expo-router. `import-review`
  // was never listed, so it rendered as a silent sixth tab — that is what
  // squeezed every label to ~57px in #531. routeCoverage.test.ts guards this.
  'import-review',
  'recipe-suggestions',
  // Retired (#768): only redirects links already sent by email.
  'this-week',
  'inventory-camera',
  'recipe-detail/[id]',
  'day-detail/[date]',
  'editProfile',
  'recognition-results',
  'taste-profile',
  'delivery-import',
  'week-recap',
  'preferences/diet',
  'preferences/staples',
  'cooking-plan/[date]',
  'photo-recipe-review',
];

/**
 * How the tab navigator answers "back".
 *
 * Every screen above, hidden or not, is a tab, so this decides where back goes
 * from a recipe, a day, the settings. React Navigation's default,
 * `'firstRoute'`, keeps only `[My Meals, current]`: Recipes → a recipe left
 * that history the same length, expo-router turned the unchanged length into
 * `history.replace`, and the Recipes entry was overwritten. Browser back and
 * `router.back()` both landed on My Meals.
 *
 * `'history'` grows with each screen visited, so each one pushes a browser
 * entry. Not `'fullHistory'`: it records param-only changes too, and back would
 * then step through every search typed on the Recipes tab.
 */
export const TAB_BACK_BEHAVIOR = 'history' as const;
